import { useEffect, useRef, useState } from 'react';
import './App.css';

const leadFormEndpoint =
  process.env.REACT_APP_LEAD_FORM_ENDPOINT || 'https://ganotras.in/myWeb/index.php';
const leadPagePath = '/valuableClientContact';

const isLeadPage = () => window.location.pathname.replace(/\/+$/, '') === leadPagePath;

const leadFormPatterns = {
  fullName: /^[A-Za-z][A-Za-z\s.'-]{1,59}$/,
  mobileNumber: /^[6-9]\d{9}$/,
  email: /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
  message: /\S/,
};

const getLeadServiceOptions = (serviceLabel = '') => {
  const normalized = (serviceLabel || '').toLowerCase();
  const options = [];

  if (normalized.includes('school')) {
    options.push('School Fee & Management Software');
  }
  if (normalized.includes('crm')) {
    options.push('CRM Software');
  }
  if (normalized.includes('hms')) {
    options.push('HMS Software');
  }

  return options;
};

const buildLeadMessage = (selectedService) => {
  if (!selectedService) {
    return '';
  }

  return `I'm interested in ${selectedService}. Please share more details.`;
};

const slides = [
  {
    image: `${process.env.PUBLIC_URL}/mainImage.png`,
    alt: 'Web and mobile app development',
    label: 'Web and mobile apps',
    title: 'Websites and apps that move your business forward.',
    description:
      'From a polished web presence to a purpose-built mobile app, create digital experiences that feel simple to use and are ready to grow with your business.',
    highlights: ['Responsive web experiences', 'iOS and Android apps', 'Product design and development'],
  },
  {
    image: `${process.env.PUBLIC_URL}/thirdImage.png`,
    alt: 'Custom school, CRM, and hospital management applications',
    label: 'School Fee and Management/ CRM/ HMS/ Custom applications',
    title: 'Software shaped around the way you work.',
    description:
      'Bring everyday operations together with tailored management systems for schools, customer relationships, healthcare, and other specialized workflows.',
    highlights: ['School management systems', 'CRM and business tools', 'Custom workflow solutions'],
  },
  {
    image: `${process.env.PUBLIC_URL}/secondImage.png`,
    alt: 'Remote server and web server services',
    label: 'Servers and support',
    title: 'A dependable foundation for your digital services.',
    description:
      'Get practical server setup, web hosting configuration, and ongoing support to keep your services reliable, available, and ready for what comes next.',
    highlights: ['Server setup and configuration', 'Web server deployment', 'Maintenance and support'],
  },
  {
    image: `${process.env.PUBLIC_URL}/forthImage.png`,
    alt: 'Network architecture services',
    label: 'Network architecture',
    title: 'Stronger connections across your workspace.',
    description:
      'Plan and build reliable LAN and Wi-Fi networks around your space, devices, and day-to-day needs, with a focus on dependable coverage and performance.',
    highlights: ['LAN planning and setup', 'Wi-Fi coverage design', 'Network configuration'],
  },
];

function App() {
  const [activeSlide, setActiveSlide] = useState(0);
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(isLeadPage);
  const [leadNotice, setLeadNotice] = useState('');
  const [leadMessage, setLeadMessage] = useState('');
  const [leadServiceOptions, setLeadServiceOptions] = useState([]);
  const [leadServiceChoice, setLeadServiceChoice] = useState('');
  const [leadEmail, setLeadEmail] = useState('');
  const [emailOtp, setEmailOtp] = useState('');
  const [emailOtpSentFor, setEmailOtpSentFor] = useState('');
  const [emailVerifiedFor, setEmailVerifiedFor] = useState('');
  const [isLeadSubmissionComplete, setIsLeadSubmissionComplete] = useState(false);
  const [isSendingEmailOtp, setIsSendingEmailOtp] = useState(false);
  const [isLeadSubmitting, setIsLeadSubmitting] = useState(false);
  const leadFormRef = useRef(null);
  const closeModalButton = useRef(null);
  const currentSlide = slides[activeSlide];

  useEffect(() => {
    const timer = window.setTimeout(() => setIsLeadModalOpen(true), 50000);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveSlide((currentSlideIndex) => (currentSlideIndex + 1) % slides.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const syncLeadPage = () => setIsLeadModalOpen(isLeadPage());
    window.addEventListener('popstate', syncLeadPage);
    return () => window.removeEventListener('popstate', syncLeadPage);
  }, []);

  useEffect(() => {
    if (!isLeadModalOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeModalButton.current?.focus();

    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setIsLeadModalOpen(false);
        setLeadNotice('');
        if (isLeadPage()) {
          window.history.replaceState(null, '', '/');
        }
      }
    };
    window.addEventListener('keydown', closeOnEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [isLeadModalOpen]);

  const showSlide = (index) => {
    setActiveSlide((index + slides.length) % slides.length);
  };

  const closeLeadModal = () => {
    setIsLeadModalOpen(false);
    setLeadNotice('');
    setLeadServiceOptions([]);
    setLeadServiceChoice('');
    setLeadMessage('');
    setLeadEmail('');
    setEmailOtp('');
    setEmailOtpSentFor('');
    setEmailVerifiedFor('');
    setIsLeadSubmissionComplete(false);
    if (isLeadPage()) {
      window.history.replaceState(null, '', '/');
    }
  };

  const openLeadModal = (serviceLabel) => {
    const options = getLeadServiceOptions(serviceLabel);
    const selectedService = options[0] || serviceLabel || '';

    setLeadServiceOptions(options);
    setLeadServiceChoice(selectedService);
    setLeadMessage(buildLeadMessage(selectedService));
    setLeadEmail('');
    setEmailOtp('');
    setEmailOtpSentFor('');
    setEmailVerifiedFor('');
    setIsLeadSubmissionComplete(false);
    setLeadNotice('');
    if (!isLeadPage()) {
      window.history.pushState(null, '', leadPagePath);
    }
    setIsLeadModalOpen(true);
  };

  const handleLeadServiceSelect = (event) => {
    const selectedService = event.target.value;
    setLeadServiceChoice(selectedService);
    setLeadMessage(buildLeadMessage(selectedService));
  };

  const postLeadAction = async (values) => {
    const response = await fetch(leadFormEndpoint, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
      },
      body: new URLSearchParams(values).toString(),
    });
    const result = await response.json();
    if (!response.ok || result.success !== true) {
      throw new Error(result.msg || 'The enquiry service rejected the request.');
    }
    return result;
  };

  const handleLeadEmailChange = (event) => {
    const nextEmail = event.target.value.trim();
    setLeadEmail(event.target.value);
    if (nextEmail !== emailOtpSentFor) {
      setEmailOtp('');
      setEmailOtpSentFor('');
      setEmailVerifiedFor('');
    }
    setLeadNotice('');
  };

  const handleSendEmailOtp = async () => {
    const email = leadEmail.trim();
    if (isLeadSubmissionComplete) return;
    if (!leadFormPatterns.email.test(email)) {
      setLeadNotice('Please enter a valid email address before requesting a verification code.');
      return;
    }
    if (isSendingEmailOtp) return;

    setIsSendingEmailOtp(true);
    setLeadNotice('Sending a verification code...');
    try {
      await postLeadAction({ action: 'sendEmailOtp', email });
      setEmailOtpSentFor(email);
      setEmailVerifiedFor('');
      setEmailOtp('');
      setLeadNotice(`A verification code was sent to ${email}.`);
    } catch (error) {
      setLeadNotice(error.message || 'Could not send a verification code. Please try again.');
    } finally {
      setIsSendingEmailOtp(false);
    }
  };

  const handleLeadSubmit = async (event) => {
    event.preventDefault();
    if (isLeadSubmitting) return;

    const form = event.currentTarget;
    const formData = new FormData(form);
    const fullName = formData.get('fullName').trim();
    const mobileNumber = formData.get('mobileNumber').trim();
    const email = formData.get('email').trim();
    const message = formData.get('message').trim();

    if (!leadFormPatterns.fullName.test(fullName)) {
      setLeadNotice('Please enter a valid full name.');
      return;
    }
    if (!leadFormPatterns.mobileNumber.test(mobileNumber)) {
      setLeadNotice('Please enter a valid 10-digit mobile number starting with 6, 7, 8, or 9.');
      return;
    }
    if (!leadFormPatterns.email.test(email)) {
      setLeadNotice('Please enter a valid email address.');
      return;
    }
    if (!leadFormPatterns.message.test(message)) {
      setLeadNotice('Please enter your message before sending the enquiry.');
      return;
    }
    if (emailVerifiedFor !== email && emailOtpSentFor !== email) {
      setLeadNotice('Send a verification code to your email before submitting the enquiry.');
      return;
    }
    if (emailVerifiedFor !== email && !/^\d{6}$/.test(emailOtp)) {
      setLeadNotice('Enter the 6-digit verification code sent to your email.');
      return;
    }

    const payload = {
      action: 'saveClientInformation',
      name: fullName,
      email,
      mobileNumber,
      subject: `Website enquiry from ${fullName}`,
      message,
    };

    setIsLeadSubmitting(true);
    setLeadNotice(emailVerifiedFor === email ? 'Sending your enquiry...' : 'Verifying your email...');
    try {
      if (emailVerifiedFor !== email) {
        await postLeadAction({ action: 'verifyEmailOtp', email, otp: emailOtp });
        setEmailVerifiedFor(email);
      }

      setLeadNotice('Sending your enquiry...');
      const result = await postLeadAction(payload);

      if (result.saved === false) {
        setLeadNotice(
          'Your enquiry was sent, but its details could not be saved. Please email hello@ganotras.in.'
        );
        return;
      }

      if (result.schoolNotice) {
        form.reset();
        setLeadEmail('');
        setEmailOtp('');
        setEmailOtpSentFor('');
        setEmailVerifiedFor('');
        setLeadMessage('');
        setIsLeadSubmissionComplete(true);
        setLeadNotice(result.schoolNotice);
        return;
      }

      form.reset();
      setLeadEmail('');
      setEmailOtp('');
      setEmailOtpSentFor('');
      setEmailVerifiedFor('');
      setIsLeadSubmissionComplete(false);
      setLeadMessage('');
      setLeadNotice('Thanks — your enquiry has been sent successfully.');
      setIsLeadModalOpen(false);
      window.location.assign('https://ganotras.in');
    } catch (error) {
      setLeadNotice(error.message || 'We could not send your enquiry. Please try again or email hello@ganotras.in.');
    } finally {
      setIsLeadSubmitting(false);
    }
  };

  return (
    <div className="app-shell">
      <header className="site-header">
        <a className="brand" href="#home" aria-label="Techlencer home">
          <span className="brand-mark" aria-hidden="true">G</span>
          <span className="brand-period">RedChilly Computers</span>
        </a>
        <nav className="main-nav" aria-label="Main navigation">
          <a href="#services">Services</a>
          <a href="#about">About</a>
          <a
            className="nav-contact"
            href={leadPagePath}
            onClick={(event) => {
              event.preventDefault();
              openLeadModal('');
            }}
          >
            Let&apos;s talk <span aria-hidden="true">↗</span>
          </a>
        </nav>
      </header>

      <main id="home">
        <section className="hero page-width" aria-labelledby="hero-title">
          <div className="hero-copy">
            <p className="eyebrow"><span /> Independent app developer</p>
            <h1 id="hero-title">
              Digital ideas,
              <br />
              <span>built to work.</span>
            </h1>
            <p className="hero-description">
              Thoughtful web apps, mobile apps, and server solutions for people
              ready to make something better.
            </p>
            <a className="primary-button" href="#services">
              Explore services <span aria-hidden="true">↓</span>
            </a>
            <p className="hero-note">Clear thinking. Clean code. Real results.</p>
          </div>

          <div className="slider" aria-label="Development services">
            <div className="slider-image-wrap">
              <img
                className="slider-image"
                src={currentSlide.image}
                alt={currentSlide.alt}
              />
              <div className="slide-caption" aria-live="polite">
                <span className="caption-dot" />
                {currentSlide.label}
              </div>
            </div>
            <div className="slider-controls">
              <span className="slide-count">
                <strong>0{activeSlide + 1}</strong> / 0{slides.length}
              </span>
              <div className="slide-dots" aria-label="Choose a banner">
                {slides.map((slide, index) => (
                  <button
                    key={slide.image}
                    className={`slide-dot${index === activeSlide ? ' is-active' : ''}`}
                    type="button"
                    aria-label={`Show banner ${index + 1}: ${slide.label}`}
                    aria-pressed={index === activeSlide}
                    onClick={() => showSlide(index)}
                  />
                ))}
              </div>
              <div className="slide-arrows">
                <button
                  type="button"
                  aria-label="Previous banner"
                  onClick={() => showSlide(activeSlide - 1)}
                >
                  ←
                </button>
                <button
                  type="button"
                  aria-label="Next banner"
                  onClick={() => showSlide(activeSlide + 1)}
                >
                  →
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="services-section" id="services" aria-labelledby="services-title">
          <div className="page-width">
            <div className="section-heading">
              <div>
                <p className="eyebrow"><span /> What I can help with</p>
                <h2 id="services-title">Small team thinking.<br />End-to-end building.</h2>
              </div>
              <p className="section-intro">
                From the first sketch to launch day and beyond, get practical
                development that fits your next step.
              </p>
            </div>
            {slides.map((slide, index) => (
              <article
                className={`service-showcase${index % 2 ? ' is-reversed' : ''}`}
                key={slide.image}
                aria-labelledby={`capability-title-${index}`}
              >
                <div className="service-showcase-image">
                  <img src={slide.image} alt={slide.alt} loading="lazy" />
                  <span className="showcase-image-label">
                    <span className="caption-dot" />
                    {slide.label}
                  </span>
                </div>
                <div className="service-showcase-copy">
                  <p className="showcase-index">CAPABILITY&nbsp; / &nbsp;0{index + 1}</p>
                  <h3 id={`capability-title-${index}`}>{slide.title}</h3>
                  <p className="showcase-description">{slide.description}</p>
                  <ul className="showcase-highlights">
                    {slide.highlights.map((highlight) => (
                      <li key={highlight}>{highlight}</li>
                    ))}
                  </ul>
                  <button
                    className="primary-button showcase-cta"
                    type="button"
                    onClick={() => openLeadModal(slide.label)}
                  >
                    Learn more <span aria-hidden="true">↗</span>
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="closing page-width" id="about">
          <p className="eyebrow"><span /> Made with care</p>
          <h2>Have an idea in mind?</h2>
          <a href="mailto:hello@gannotra.com" className="text-link">
            Let&apos;s make it happen <span aria-hidden="true">↗</span>
          </a>
        </section>
      </main>

      <footer className="site-footer">
        <div className="footer-main page-width">
          <div className="footer-about">
            <a className="brand footer-brand" href="#home">
              <span className="brand-mark" aria-hidden="true">G</span>
              <span>techylencer By RedChilly Computers<span className="brand-period">.</span></span>
            </a>
            <p>Thoughtful digital solutions, built with care.</p>
          </div>
          <div className="footer-detail">
            <h2>Location</h2>
            <address>
              Ferozepur, Punjab 152002
              <br />
              India
            </address>
            <iframe
              className="footer-map"
              title="Redchilly Computers location in Ferozepur"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3421.954983068388!2d74.62414137635966!3d30.943821774483162!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3919e941fd553187%3A0x29690cbd95138230!2sRedchilly%20Computers!5e0!3m2!1sen!2sin!4v1791252775165!5m2!1sen!2sin"
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
            <a
              className="footer-link"
              href="https://www.google.com/maps/search/?api=1&query=Ferozepur%2C%20Punjab%20152002%2C%20India"
              target="_blank"
              rel="noreferrer"
            >
              View on map <span aria-hidden="true">↗</span>
            </a>
          </div>
          <div className="footer-detail">
            <h2>Get in touch</h2>
            <a className="footer-link" href="mailto:hello@ganotras.in">
              hello@ganotras.in
            </a>
            <p>Web &amp; mobile app development</p>
          </div>
        </div>
        <div className="footer-bottom page-width">
          <span>© {new Date().getFullYear()} Techlencer By RedChilly Computers. All rights reserved.</span>
          <a href="#home">Back to top ↑</a>
        </div>
      </footer>

      {isLeadModalOpen && (
        <div
          className="lead-modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeLeadModal();
          }}
        >
          <section
            className="lead-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="lead-modal-title"
            aria-describedby="lead-modal-description"
          >
            <button
              ref={closeModalButton}
              className="lead-modal-close"
              type="button"
              aria-label="Close contact form"
              onClick={closeLeadModal}
            >
              ×
            </button>
            <div className="lead-modal-brand">
              <span className="lead-modal-brand-mark" aria-hidden="true">R</span>
              <div>
                <p className="lead-modal-company">TechyLencer (By RedChilly Computers)</p>
                <p className="lead-modal-tagline">Technology, thoughtfully delivered</p>
              </div>
            </div>
            <p className="eyebrow"><span /> Project enquiries</p>
            <h2 id="lead-modal-title">Let&apos;s build something that works.</h2>
            <p className="lead-modal-description" id="lead-modal-description">
              Leave your details and I&apos;ll be in touch about your project.
            </p>
            <form ref={leadFormRef} className="lead-form" onSubmit={handleLeadSubmit}>
              {leadServiceOptions.length > 0 && (
                <label>
                  Select software
                  <select value={leadServiceChoice} onChange={handleLeadServiceSelect}>
                    {leadServiceOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label>
                Full name
                <input
                  name="fullName"
                  type="text"
                  autoComplete="name"
                  pattern="^[A-Za-z][A-Za-z\s.'-]{1,59}$"
                  minLength="2"
                  maxLength="60"
                  title="Enter a valid full name with letters only, spaces, apostrophes, periods, or hyphens."
                  placeholder="John Doe"
                  required
                />
              </label>
              <label>
                Mobile number
                <input
                  name="mobileNumber"
                  type="tel"
                  autoComplete="tel"
                  inputMode="tel"
                  pattern="^[6-9]\d{9}$"
                  minLength="10"
                  maxLength="10"
                  title="Enter a 10-digit mobile number starting with 6, 7, 8, or 9."
                  placeholder="9876543210"
                  required
                />
              </label>
              <label>
                How can I help?
                <textarea
                  name="message"
                  rows="3"
                  value={leadMessage}
                  onChange={(event) => setLeadMessage(event.target.value)}
                  pattern="\S+"
                  minLength="3"
                  title="Please enter a brief message with at least 3 characters."
                  required
                />
              </label>
              <label className="lead-email-label" htmlFor="lead-email">
                Email address
                <span className="lead-email-group">
                  <input
                    id="lead-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    pattern="^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$"
                    title="Enter a valid email address, such as name@example.com."
                    placeholder="you@example.com"
                    value={leadEmail}
                    onChange={handleLeadEmailChange}
                    required
                  />
                  <button
                    className="primary-button lead-send-code"
                    type="button"
                    onClick={handleSendEmailOtp}
                    disabled={isSendingEmailOtp || isLeadSubmitting || isLeadSubmissionComplete}
                  >
                    {isSendingEmailOtp ? 'Sending...' : emailOtpSentFor === leadEmail.trim() ? 'Resend code' : 'Send code'}
                  </button>
                </span>
              </label>
              {emailOtpSentFor && emailOtpSentFor === leadEmail.trim() && (
                <label>
                  Email verification code
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]{6}"
                    minLength="6"
                    maxLength="6"
                    title="Enter the 6-digit code sent to your email."
                    placeholder="6-digit code"
                    value={emailOtp}
                    onChange={(event) => setEmailOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
                    required={emailVerifiedFor !== leadEmail.trim()}
                  />
                </label>
              )}
              <button
                className="primary-button lead-submit"
                type="submit"
                disabled={isLeadSubmitting || isLeadSubmissionComplete}
              >
                {isLeadSubmissionComplete
                  ? 'Enquiry saved'
                  : isLeadSubmitting
                  ? emailVerifiedFor === leadEmail.trim() ? 'Sending...' : 'Verifying...'
                  : emailVerifiedFor === leadEmail.trim() ? 'Send enquiry' : 'Verify & send enquiry'}
                {' '}<span aria-hidden="true">↗</span>
              </button>
            </form>
            {leadNotice && (
              <p className="lead-notice" role="status">{leadNotice}</p>
            )}
            <p className="lead-form-note">
              Your enquiry will be sent to our team. We&apos;ll only use your details to respond.
            </p>
          </section>
        </div>
      )}
    </div>
  );
}

export default App;
